"use client";

import { cn } from "@/utils";
import { useEffect, useState } from "react";

interface MobileOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  variant?: "blur" | "gradient" | "glass" | "subtle" | "clear";
  className?: string;
}

/**
 * Beautiful mobile overlay component with various visual effects
 */
export function MobileOverlay({
  isOpen,
  onClose,
  variant = "glass",
  className = "",
}: MobileOverlayProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      // Delay hiding to allow exit animation
      const timeout = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-50 bg-black/30 lg:hidden transition-all duration-300",
        "cursor-pointer",
        isOpen ? "opacity-100" : "opacity-0",
        className
      )}
      onClick={onClose}
      style={{
        background:
          variant === "gradient"
            ? "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(59,130,246,0.1) 50%, rgba(99,102,241,0.15) 100%)"
            : undefined,
      }}
      role="button"
      aria-label="Close sidebar"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === "Escape" || e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClose();
        }
      }}
    >
      {/* Optional decorative elements */}
      {variant === "glass" && (
        <>
          {/* Subtle grid pattern */}
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
                linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
              `,
              backgroundSize: "20px 20px",
            }}
          />

          {/* Floating particles effect */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white/20 rounded-full animate-pulse"
                style={{
                  left: `${20 + i * 15}%`,
                  top: `${30 + i * 10}%`,
                  animationDelay: `${i * 0.5}s`,
                  animationDuration: `${2 + i * 0.5}s`,
                }}
              />
            ))}
          </div>
        </>
      )}

      {/* Tap to close hint - appears briefly when overlay opens */}
      {/* {isOpen && (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 pointer-events-none">
          <div
            className="bg-black/30 backdrop-blur-sm rounded-full px-4 py-2 transition-all duration-1000"
            style={{
              animation: 'fadeInOut 4s ease-in-out forwards',
            }}
          >
            <p className="text-white text-sm font-medium">Tap anywhere to close</p>
          </div>
        </div>
      )} */}

      {/* CSS Animation for hint */}
      <style jsx>{`
        @keyframes fadeInOut {
          0% {
            opacity: 0;
            transform: translateY(10px);
          }
          20% {
            opacity: 1;
            transform: translateY(0px);
          }
          80% {
            opacity: 1;
            transform: translateY(0px);
          }
          100% {
            opacity: 0;
            transform: translateY(-10px);
          }
        }
      `}</style>
    </div>
  );
}

/**
 * Preset overlay variants for common use cases
 */
export const OverlayVariants = {
  // No overlay - completely transparent
  Clear: (props: Omit<MobileOverlayProps, "variant">) => (
    <MobileOverlay {...props} variant="clear" />
  ),

  // Subtle blur for minimal distraction
  Subtle: (props: Omit<MobileOverlayProps, "variant">) => (
    <MobileOverlay {...props} variant="subtle" />
  ),

  // Glass morphism effect
  Glass: (props: Omit<MobileOverlayProps, "variant">) => (
    <MobileOverlay {...props} variant="glass" />
  ),

  // Gradient overlay with color
  Gradient: (props: Omit<MobileOverlayProps, "variant">) => (
    <MobileOverlay {...props} variant="gradient" />
  ),

  // Strong blur effect
  Blur: (props: Omit<MobileOverlayProps, "variant">) => <MobileOverlay {...props} variant="blur" />,
};

/**
 * Hook for managing overlay state
 */
export function useMobileOverlay() {
  const [isOpen, setIsOpen] = useState(false);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);
  const toggle = () => setIsOpen(!isOpen);

  return {
    isOpen,
    open,
    close,
    toggle,
  };
}
